import { Component, OnInit, ViewChild, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, NgForm } from '@angular/forms';
import { catchError, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { SignInModel } from '../shared/sign-in-model';
import { ROUTE_PRIVACY_STATEMENT, ROUTE_SIGNUP, ROUTE_TERMS_CONDITIONS } from '../shared/routes';
import { AuthenticationService } from '../shared/authentication.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { Response } from 'src/app/shared/services/response';
import { ROUTE_FORGOT_PASSWORD } from 'src/app/forgot-password/shared/routes';
import { ErrorModel } from 'src/app/shared/models/error.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { ROUTE_HELP } from 'src/app/help/shared/routes';
import { of } from 'rxjs';
import { StorageService } from 'src/app/shared/storage/storage.service';
import { RecaptchaEnterpriseService } from 'src/app/shared/recaptcha-enterprise/recaptcha-enterprise.service';
import { CompanyInterestService } from 'src/app/registration/shared/company-interest.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import {
  EMAIL_ALREADY_VERIFIED_CODE,
  RESEND_VERIFICATION_COOLDOWN_MS,
  RESEND_VERIFICATION_ERROR_MESSAGE,
  USER_NOT_CONFIRMED_CODE,
  VERIFICATION_EMAIL_SENT_CODE,
  VERIFICATION_EMAIL_SENT_MESSAGE,
  VERIFICATION_REQUIRED_MESSAGE
} from '../shared/email-verification';

const ACTION_SIGN_IN = 'Sign in';
const RequiredFieldMessage = 'You must enter a value';
const InvalidEmailAddress = 'Invalid email address';
const MinimumLengthFieldMessage = (value: number) => `Must be at least ${value} characters`;
const SignInForm = 'SignFormDetails';

@Component({
    selector: 'app-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
    standalone: false
})
export class SignInComponent implements OnInit, AfterViewInit, OnDestroy {
  form: UntypedFormGroup;
  hidePassword: boolean;
  loading: boolean;
  errorMessage: string;
  verificationRequired = false;
  resendVerificationLoading = false;
  resendVerificationCooldown = false;
  @ViewChild('email', { static: false }) emailInput: any;

  private subs = new SubSink();
  private resendCooldownTimer: ReturnType<typeof setTimeout>;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly authenticationApi: AuthenticationService,
    private readonly logger: LoggerService,
    private readonly elementRef: ElementRef,
    private readonly timeService: DateTimeService,
    private readonly recaptchaEnterpriseService: RecaptchaEnterpriseService,
    private readonly storageService: StorageService,
    private readonly companyInterestService: CompanyInterestService,
    private readonly toastService: ToastService
  ) {
    this.hidePassword = true;
    this.loading = false;
    this.errorMessage = undefined;

    this.form = this.createForm();
  }

  ngOnInit() {
    this.companyInterestService.clearInviteApplyPendingBridge();
  }

  ngAfterViewInit(): void {
    this.elementRef.nativeElement.ownerDocument.body.style.backgroundColor = '#0B184E';

    setTimeout(() => {
      this.emailInput?.nativeElement?.focus();
    }, 0);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    clearTimeout(this.resendCooldownTimer);
    this.elementRef.nativeElement.ownerDocument.body.style.backgroundColor = '#fff';
  }

  onSubmit(signinForm: NgForm) {
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    const model = this.prepareSaveModel();

    this.subs.add(
      this.recaptchaEnterpriseService
        .execute('login')
        .pipe(
          switchMap(token => {
            return this.authenticationApi.login(model, token);
          }),
          catchError((error: any) => {
            if (error?.code === USER_NOT_CONFIRMED_CODE) {
              this.verificationRequired = true;
              this.errorMessage = VERIFICATION_REQUIRED_MESSAGE;
            } else {
              this.verificationRequired = false;
              this.errorMessage = error?.message;
            }
            this.loading = false;

            return of(false);
          })
        )
        .subscribe()
    );
  }

  resendVerificationEmail(): void {
    if (this.resendVerificationLoading || this.resendVerificationCooldown) {
      return;
    }

    const username = (this.email.value as string).trim();
    this.resendVerificationLoading = true;
    this.startResendCooldown();

    this.subs.add(
      this.authenticationApi.resendVerificationEmail(username).subscribe({
        next: response => {
          this.resendVerificationLoading = false;

          if (response.code === VERIFICATION_EMAIL_SENT_CODE) {
            this.toastService.showSuccess(VERIFICATION_EMAIL_SENT_MESSAGE);
          } else if (response.code === EMAIL_ALREADY_VERIFIED_CODE) {
            this.verificationRequired = false;
            this.errorMessage = undefined;
            this.toastService.showInfo('Your email is already verified. Please sign in.');
          } else {
            this.toastService.showError(RESEND_VERIFICATION_ERROR_MESSAGE);
          }
        },
        error: () => {
          this.resendVerificationLoading = false;
          this.toastService.showError(RESEND_VERIFICATION_ERROR_MESSAGE);
        }
      })
    );
  }

  onNavigateToSignUp(): void {
    this.router.navigate([`/${ROUTE_SIGNUP}`]);
  }

  onNavigateToForgotPassword(): void {
    this.router.navigate([`/${ROUTE_FORGOT_PASSWORD}`]);
  }

    onNavigateToTermsConditions(): void {
      const model = this.prepareSaveModel();
      this.storageService.store(SignInForm, model);
      this.router.navigate([`/${ROUTE_TERMS_CONDITIONS}`]);
    }
  
    onNavigateToPrivacyStatement(): void {
      const model = this.prepareSaveModel();
      this.storageService.store(SignInForm, model);
      this.router.navigate([`/${ROUTE_PRIVACY_STATEMENT}`]);
    }

  onNavigateToHelp(): void {
    this.router.navigate([`/${ROUTE_HELP}`]);
  }

  get email() {
    return this.form.get('email');
  }
  get password() {
    return this.form.get('password');
  }

  getEmailErrorMessage() {
    const errors = this.email.errors;
    return errors.required ? RequiredFieldMessage : errors.pattern ? InvalidEmailAddress : '';
  }

  getPasswordErrorMessage() {
    const errors = this.password.errors;
    return errors.required
      ? RequiredFieldMessage
      : errors.minlength
      ? MinimumLengthFieldMessage(errors.minlength.requiredLength)
      : '';
  }

  private createForm(): UntypedFormGroup {
    const form = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\\.[A-Za-z0-9]+)((\\.[A-Za-z]{2,}){0,1})\\s*$')
        ]
      ],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });

    return form;
  }

  private prepareSaveModel(): SignInModel {
    const formModel = this.form.value;

    const model = {
      username: (formModel.email as string).trim(),
      password: formModel.password as string
    };

    return model as SignInModel;
  }

  private startResendCooldown(): void {
    this.resendVerificationCooldown = true;
    clearTimeout(this.resendCooldownTimer);
    this.resendCooldownTimer = setTimeout(() => {
      this.resendVerificationCooldown = false;
    }, RESEND_VERIFICATION_COOLDOWN_MS);
  }
}
